import { autoinject } from "aurelia-dependency-injection";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { PTDateTimeHelper } from "../../helpers/pt-datetime-helper";
import { NewPortCallTypeModel } from "../../models/create-vessel-port-arrival-model";
import { PortsTerminalBerthsResponseItem, PortTerminalAndBerthResponse } from "../../models/ports-and-terminals.models";
import { PortsAndTerminalsService } from "../../services/ports-and-terminals.service";

@autoinject
export class UpdateVesselPortDetailsDialog {
    public vesselName: string;
    public vesselImoNumber: string;
    onSave: () => void;
    ports: PortsTerminalBerthsResponseItem[] = [];
    terminals: PortsTerminalBerthsResponseItem[] = [];
    berths: PortsTerminalBerthsResponseItem[] = [];
    allTerminals: PortsTerminalBerthsResponseItem[] = [];
    allBerths: PortsTerminalBerthsResponseItem[] = [];

    model: NewPortCallTypeModel = {
        portCallId: null,
        portSKey: null,
        berthSKey: null,
        terminalSKey: null,
        arrivalDate: null,
        departureDate: null,
        arrivalTime: null,
        departureTime: null,
        vesselSKey: null
    }

    constructor(private dialogClientService: DialogClientService,
        private portsAndTerminalsService: PortsAndTerminalsService,
        private validationController: ValidationController,
        private notificationClientService: NotificationClientService) {
        this.validationController.reset();
        this.validationController.addRenderer(new CustomValidationRenderer());
    }

    activate(model: { item, portTerminalAndBerthResponse: PortTerminalAndBerthResponse, onSave: () => void }) {
        this.vesselName = model?.item?.vesselName;
        this.vesselImoNumber = `${model.item.vesselIdentifierType} ${model.item.vesselIdentifier}`;

        this.model.portCallId = model?.item?.id;
        this.model.vesselSKey = model?.item?.vesselSKey;
        this.model.portSKey = model?.item?.portSKey;
        this.model.terminalSKey = model?.item?.terminalSKey;
        this.model.berthSKey = model?.item?.berthSKey;
        this.model.departureDate = PTDateTimeHelper.getDateWithoutTimezoneInfo(model?.item?.etd);
        this.model.arrivalDate = PTDateTimeHelper.getDateWithoutTimezoneInfo(model?.item?.eta);
        this.model.departureTime = PTDateTimeHelper.getDateWithoutTimezoneInfo(model.item?.etd);
        this.model.arrivalTime = PTDateTimeHelper.getDateWithoutTimezoneInfo(model.item?.eta);

        this.onSave = model.onSave;
        this.loadPortsTerminalsAndBerths(model.portTerminalAndBerthResponse);
    }

    close() {
        this.dialogClientService.hide();
    }

    submit() {
        return new Promise<void>((resolve, reject) => {
            this.initValidationRules();
            this.validationController.validate().then(async response => {
                if (!response.valid) {
                    resolve();
                    return;
                }

                const portOffset = await this.portsAndTerminalsService.getPortOffset(this.model.portSKey);
                
                const updateModel = CloneHelper.cloneObject(this.model);
                updateModel.arrivalDate = PTDateTimeHelper.convertToUtc(this.model.arrivalDate, this.model.arrivalTime, portOffset);
                updateModel.departureDate = PTDateTimeHelper.convertToUtc(this.model.departureDate, this.model.departureTime, portOffset);

                this.portsAndTerminalsService.updateVesselPortArrival({ vesselPortCalls: [updateModel] })
                    .then(() => {
                        this.notificationClientService.show(``, `Vessel port details updated successfully`, NotificationType.Success);
                        this.close();
                        if (this.onSave) {
                            this.onSave();
                        }
                        resolve();
                    })
                    .catch(e => {
                        this.notificationClientService.show(``, `Failed to update vessel port details`, NotificationType.Error);
                        reject(e);
                    });
            });

        });
    }

    initValidationRules() {
        ValidationRules
            .ensure<NewPortCallTypeModel, number>(vpa => vpa.portSKey)
            .displayName('Port')
            .required()
            .on(this.model);
    }

    loadPortsTerminalsAndBerths(portTerminalAndBerthResponse: PortTerminalAndBerthResponse) {
        if (portTerminalAndBerthResponse) {
            this.setPortsTerminalsAndBerths(portTerminalAndBerthResponse);
        }
        else {
            //fallback
            this.portsAndTerminalsService.loadPortsTerminalsAndBerths()
                .then(res => {
                    this.setPortsTerminalsAndBerths(res);
                });
        }

    }

    setPortsTerminalsAndBerths(res: PortTerminalAndBerthResponse) {
        this.ports = res.ports;
        this.allTerminals = res.terminals;
        this.terminals = res.terminals;
        this.allBerths = res.berths;
        this.berths = res.berths;
    }

    getTerminals($event) {
        if ($event && $event.detail && $event.detail.item) {
            this.terminals = this.allTerminals.filter(x => x.parentId == $event.detail.item.id);
            if (this.terminals.findIndex(x => x.id == this.model.terminalSKey) < 0) {
                this.model.terminalSKey = null
                this.model.berthSKey = null
            }
        }

    }

    getBerths($event) {
        if ($event && $event.detail && $event.detail.item) {
            this.berths = this.allBerths.filter(x => x.parentId == $event.detail.item.id);
            if (this.berths.findIndex(x => x.id == this.model.berthSKey) < 0) {
                this.model.berthSKey = null
            }
        }
    }

}